// INSTRUCTIONS
// each child of this component will become a page
// each child should have a title and a function to navigate forward (unless its the last child)

import React, {
  ReactElement,
  ReactNode,
  useRef,
  useState,
  useEffect,
  Children,
  useMemo,
  createContext
} from "react";
import ReactDOM from "react-dom";

import { CSSTransition } from "react-transition-group";

import CancelOutlined from "@material-ui/icons/CancelOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import styles from "styles/Modal.module.scss";
import useMediaQuery from "helpers/useMediaQuery";
import { useTransition, animated } from "@react-spring/web";

export const ModalContext = createContext(null);

const MultiPageModal = ({
  show,
  closeButtonHidden = false,
  disableCloseOnOutsideClick = false,
  onClose,
  width,
  maxWidth,
  height,
  maxHeight,
  children,
  className = "",
  startPage = 0,
  navLocked = false
}) => {
  const nodeRef = useRef(null);
  const isNotMobile = useMediaQuery(`(min-width: ${width})`);

  const [isBrowser, setIsBrowser] = useState(false);
  const [currentPage, setCurrentPage] = useState(startPage);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const modalNavBack = () => {
    setReverse(true);
    setCurrentPage((prev) => prev - 1);
  };

  const modalNavForward = () => {
    setReverse(false);
    setCurrentPage((prev) => prev + 1);
  };

  const onUserClosedModal = () => {
    onClose();
    setCurrentPage(startPage);
  };

  const transitions = useTransition(currentPage, {
    from: {
      opacity: 0,
      overflow: "hidden",
      transform: `translate3d(${reverse ? "-100%" : "100%"},0,0)`
    },
    enter: {
      opacity: 1,
      overflow: "hidden",
      transform: "translate3d(0%,0,0)"
    },
    leave: {
      opacity: 0,
      overflow: "hidden",
      transform: `translate3d(${reverse ? "100%" : "-100%"},0,0)`
    },
    // "initial: null" should only disable the 'from' initial transition, not the subsequent 'leave' transition (on the first manually triggered transition)
    initial: null
  });

  if (isBrowser) {
    return ReactDOM.createPortal(
      <CSSTransition
        nodeRef={nodeRef}
        in={show}
        unmountOnExit
        timeout={{ enter: 0, exit: 300 }}
        classNames={{
          enterDone: styles.wapperEnterDone,
          exit: styles.wrapperExit
        }}
      >
        <div
          ref={nodeRef}
          className={`${className} ${styles.modalWrapper}`}
          onClick={disableCloseOnOutsideClick ? undefined : onUserClosedModal}
        >
          <div
            className={styles.modalContent}
            style={
              isNotMobile
                ? {
                    height: height,
                    width: width,
                    maxHeight: maxHeight,
                    maxWidth: maxWidth
                  }
                : {}
            }
            onClick={(event) => event.stopPropagation()}
          >
            {closeButtonHidden ? (
              ""
            ) : (
              <div className={styles.closeButton} onClick={onUserClosedModal}>
                <CancelOutlined style={{ fontSize: 30 }} />
              </div>
            )}
            <div className={styles.container}>
              <div className={styles.header}>
                <div className={styles.leftHeader}>
                  {currentPage > 0 && !navLocked ? (
                    <div className={styles.backButton} onClick={modalNavBack}>
                      <ChevronLeftIcon />
                      {Children.toArray(children)[currentPage - 1].props.title}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <div className={styles.title}>
                  {Children.toArray(children)[currentPage].props.title}
                </div>
                <div className={styles.rightHeader}></div>
              </div>

              <div className={styles.content}>
                <ModalContext.Provider
                  value={{
                    onCloseModal: onUserClosedModal,
                    onNavigateForward: modalNavForward
                  }}
                >
                  {transitions((style, i) => {
                    return (
                      <animated.div
                        style={{ ...style, width: "100%", height: "100%" }}
                      >
                        {Children.toArray(children)[i]}
                      </animated.div>
                    );
                  })}
                </ModalContext.Provider>
              </div>
            </div>
          </div>
        </div>
      </CSSTransition>,
      document.getElementById("root")
    );
  } else {
    return null;
  }
};

export default MultiPageModal;
